import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { withRouter, useLocation } from "react-router-dom";
import ContainerEdit from "./ContainerEdit";
import ContainerFilter from "./ContainerFilter";
import ContainerRow from "./ContainerRow";
import ContainerHeader from "./ContainerHeader";
import ContainerImageZoom from "./ContainerImageZoom";
import { Card } from "reactstrap";
import Cookies from "js-cookie";
import MapDisplay from "../../components/utils/Map";
import TruckSvg from "../../components/utils/TruckSvg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  checkContainerContractExist,
  checkIfAllSelected,
  checkPlanningIsActive,
  handleDeselectAll,
  sendPlanningMail,
} from "./utilsTable";
import { ConfirmationModal } from "../../components/modals/ConfirmationModal";
import {
  formattedTypeContainer,
  formattedStatus,
} from "../../components/formatted/FormattedText";
import {
  getContainersList,
  getSuperContainerList,
  getHistoryFillingSpeed,
  getHistoryCycleContainer,
  getContainerFillingAlert,
} from "../../services/container";
import { getSettingsData } from "../../services/settings";
import { useTranslation } from "react-i18next";
import ForbiddenModule from "../../components/ForbiddenModule";
import FillingCalendar from "./FillingCalendar";
import PageHeader from "../../components/PageHeader";
import ActivityHistory from "./ActivityHistory";
import Pagination from "../../components/utils/Pagination";
import { ENRICHMENT_BATCH_SIZE } from "../../constants/cache";
import { UI_CONSTANTS } from "../../constants/ui";
import useEffectiveWidth from "../../hooks/useEffectiveWidth";

const LEGACY_MONITORING_CACHE_KEYS = [
  "monitoring_containers_cache",
  "monitoring_containers_enrichment_cache",
  "monitoring_containers_cycle_cache",
  "monitoring_containers_alert_cache",
];

function parseEnrichmentResponses(
  enrichmentResponse,
  cycleResponse,
  alertResponse,
  container,
) {
  let enrichment = null;
  let cycle = null;
  let alert = null;

  if (
    enrichmentResponse.status === "fulfilled" &&
    enrichmentResponse.value?.status === 200 &&
    enrichmentResponse.value?.data
  ) {
    const timezone =
      container?.addressJoin?.timezone ||
      container?.addressTimezone ||
      "Europe/Paris";
    enrichment = {
      durationFullFillingMs:
        enrichmentResponse.value.data.durationFullFillingMs.map((item) => ({
          ...item,
          timezone,
        })),
      reliablePrediction: enrichmentResponse.value.data.reliablePrediction,
      fromDate: enrichmentResponse.value.data.fromDate,
      meanFillingLevelByOpenedDay:
        enrichmentResponse.value.data.meanFillingLevelByOpenedDay,
      meanFillingLevelByOpenedHour:
        enrichmentResponse.value.data.meanFillingLevelByOpenedHour,
      meanFillingLevelByDay:
        enrichmentResponse.value.data.meanFillingLevelByDay,
    };
  }

  if (
    cycleResponse.status === "fulfilled" &&
    cycleResponse.value?.status === 200 &&
    cycleResponse.value?.data
  ) {
    cycle = {
      historyCycle: cycleResponse.value.data.historyCycle,
      numberOfStars: cycleResponse.value.data.numberOfStars,
      numberOfAnomaly: cycleResponse.value.data.numberOfAnomaly,
      anomalyDelivery: cycleResponse.value.data.anomalyDelivery,
      numberOfPackmat: cycleResponse.value.data.numberOfPackmat,
      totalVolumeSortingError: cycleResponse.value.data.totalVolumeSortingError,
      totalWasteVolume: cycleResponse.value.data.totalWasteVolume,
      totalMassSortingError: cycleResponse.value.data.totalMassSortingError,
      totalWasteMass: cycleResponse.value.data.totalWasteMass,
      levelLastRotation: cycleResponse.value.data.levelLastRotation,
      lastRotation: cycleResponse.value.data.lastRotation,
      lastPackmatEfficiency: cycleResponse.value.data.lastPackmatEfficiency,
      objectDelivery: cycleResponse.value.data.objectDelivery,
      numberOfObjects: cycleResponse.value.data.numberOfObjects,
    };
  }

  if (
    alertResponse.status === "fulfilled" &&
    alertResponse.value?.status === 200 &&
    alertResponse.value?.data
  ) {
    alert = alertResponse.value.data;
  }

  if (!enrichment || !cycle || !alert) {
    return null;
  }

  return { enrichment, cycle, alert };
}

function mergeEnrichmentIntoContainer(container, entry) {
  if (!entry?.enrichment || !entry?.cycle || !entry?.alert) {
    return container;
  }

  const { enrichment, cycle, alert } = entry;

  return {
    ...container,
    historyCurrent: cycle.historyCycle,
    numberOfStars: cycle.numberOfStars,
    numberOfAnomaly: cycle.numberOfAnomaly,
    anomalyDelivery: cycle.anomalyDelivery,
    numberOfPackmat: cycle.numberOfPackmat,
    totalVolumeSortingError: cycle.totalVolumeSortingError,
    totalWasteVolume: cycle.totalWasteVolume,
    totalMassSortingError: cycle.totalMassSortingError,
    totalWasteMass: cycle.totalWasteMass,
    levelLastRotation: cycle.levelLastRotation?.[0],
    dateLastRotation: cycle.historyCycle?.[cycle.historyCycle.length - 1],
    lastRotation: cycle.lastRotation?.[0],
    lastPackmatEfficiency: cycle.lastPackmatEfficiency?.[0],
    objectDelivery: cycle.objectDelivery,
    numberOfObjects: cycle.numberOfObjects,
    durationFullFillingMs: enrichment.durationFullFillingMs,
    reliablePrediction: enrichment.reliablePrediction,
    fromDate: enrichment.fromDate,
    meanFillingLevelByOpenedDay: enrichment.meanFillingLevelByOpenedDay,
    meanFillingLevelByOpenedHour: enrichment.meanFillingLevelByOpenedHour,
    meanFillingLevelByDay: enrichment.meanFillingLevelByDay,
    fillingAlert: alert,
  };
}

const Container = () => {
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");
  const corporationModules = JSON.parse(
    localStorage.getItem("corporationModules"),
  );

  const [monitoring, setMonitoring] = useState(true);

  useEffect(() => {
    if (corporationModules !== null) {
      setMonitoring(corporationModules.showMonitoringModule > 0);
    }
  }, [corporationModules]);

  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";

  const { t } = useTranslation();

  const location = useLocation();
  const stateContainer = location.state || undefined;
  const limitFilling = stateContainer?.limitFilling ?? false;
  const keywordF = stateContainer?.keywordF ?? null;
  const typeFillingF = stateContainer?.typeFilling ?? null;
  const addressF = stateContainer?.addressF ?? null;

  const [addressKeysFiltered, setAddressKeysFiltered] = useState(
    addressF
      ? [
          {
            key: addressF,
            cat: "Adresse",
            type: "address",
          },
        ]
      : [],
  );
  const [arrayFilterFillingLevel, setArrayFilterFIllingLevel] = useState(
    limitFilling || [0, 100],
  );
  const [contentKeysFiltered, setContentKeysFiltered] = useState([]);
  const [hourKeysFiltered, setHourKeysFiltered] = useState([]);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchTextDebounced, setSearchTextDebounced] = useState("");
  const [fullData, setFullData] = useState([]);
  const [displayToBlock, setDisplayToBlock] = useState(
    stateContainer !== undefined && stateContainer.displayTimeLine
      ? true
      : false,
  );
  const [fieldSort, setFieldSort] = useState("filling_level");
  const [fillingRateDeliveryMap, setFillingRateDeliveryMap] = useState([]);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [modalEdit, setModalEdit] = useState(false);
  const [keywordFilterOptions, setKeywordFilterOptions] = useState([]);
  const [keywordFilter, setKeywordFilter] = useState(
    keywordF ? [{ type: "id", key: keywordF.toString() }] : [],
  );
  const [typeFillingFilter, setTypeFillingFilter] = useState(
    typeFillingF ? typeFillingF : "all",
  );
  const [settings, setSettings] = useState([]);
  const [statusKeysFiltered, setStatusKeysFiltered] = useState([]);
  const [orderSort] = useState("-1");
  const [typeKeysFiltered, setTypeKeysFiltered] = useState([]);
  const [volumeKeysFiltered, setVolumeKeysFiltered] = useState([]);
  const [positioningKeysFiltered, setPositioningKeysFiltered] = useState([]);
  const [containersSelected, setContainersSelected] = useState([]);
  const [confimationCollectModal, setConfirmationCollectModal] =
    useState(false);
  const [helpButton, setHelpButton] = useState(false);
  const screenWidth = useEffectiveWidth();
  const [noActivePackmat, setNoActivePackmat] = useState(false);
  const [superContainers, setSuperContainers] = useState({});
  const [containerObjectIdList, setContainerObjectIdList] = useState([]);

  const [cleanAllFilters, setCleanAllFilters] = useState(false);

  const [displayMode, setDisplayMode] = useState(
    Cookies.get("containerDisplayMode") || "table",
  );

  const [isImageZoomOverlayActive, setIsImageZoomOverlayActive] =
    useState(false);
  const [containerActive, setContainerActive] = useState({});
  const [containerInactive, setContainerInactive] = useState([]);
  const [showContainerInactive, setShowContainerInactive] = useState(false);

  const [activeMonitoringTab, setActiveMonitoringTab] = useState("zones");
  const [shareButton, setShareButton] = useState(null);
  const [enrichmentById, setEnrichmentById] = useState({});
  const [isEnriching, setIsEnriching] = useState(false);
  const isEnrichingRef = useRef(false);
  const [enrichmentErrors, setEnrichmentErrors] = useState(new Set());
  const [lastEnrichmentTime, setLastEnrichmentTime] = useState(null);

  useEffect(() => {
    LEGACY_MONITORING_CACHE_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    });
  }, []);

  const getActiveItems = (datas, alertNumberOfDays) => {
    return datas.filter((item) => {
      const deltaAlert = Math.floor(
        (Date.now() - new Date(item._time).getTime()) / 86400000,
      );
      return deltaAlert <= alertNumberOfDays;
    });
  };
  // pull out the list of selected IDs
  const selectedIds = containersSelected.map((c) => c._id);

  const warningContainers = data.filter(
    (d) =>
      selectedIds.includes(d._id) &&
      d.alertLight?.alertLightLastMessage === "GYRO_RED+BUZZER",
  );
  const names = warningContainers.map((d) => d.wasteName).join(", ");

  const modalMessage = names
    ? // pass the joined names into the `names` interpolation key
      t("confirmRotationWithWarning", { names })
    : t("confirmRotation");
  const filterData = useCallback(
    async (fullDataIn, alertNumberOfDays, enrichment = {}) => {
      let filteredData = fullDataIn.map((container) =>
        mergeEnrichmentIntoContainer(container, enrichment[container._id]),
      );

      if (searchTextDebounced && searchTextDebounced.trim()) {
        const search = searchTextDebounced.trim().toLowerCase();
        filteredData = filteredData.filter((item) => {
          const addressMatch =
            item.addressName &&
            String(item.addressName).toLowerCase().includes(search);
          const fluxMatch =
            (item.contentCategoryJoin?.label &&
              String(item.contentCategoryJoin.label)
                .toLowerCase()
                .includes(search)) ||
            (item.wasteName &&
              String(item.wasteName).toLowerCase().includes(search));
          return addressMatch || fluxMatch;
        });
      }

      if (positioningKeysFiltered.length > 0) {
        filteredData = filteredData.filter((item) => {
          return positioningKeysFiltered.some(
            (positioning) => positioning.name === item.positioning,
          );
        });
      }

      if (addressKeysFiltered.length > 0) {
        filteredData = filteredData.filter((item) => {
          return addressKeysFiltered.some(
            (address) => address.key === item.addressName,
          );
        });
      }

      if (
        !arrayFilterFillingLevel.every((value, index) => {
          return value === [0, 100][index];
        })
      ) {
        filteredData = filteredData.filter((item) => {
          return (
            item.filling_level >= arrayFilterFillingLevel[0] &&
            item.filling_level <= arrayFilterFillingLevel[1]
          );
        });
      }

      if (statusKeysFiltered.length > 0) {
        filteredData = filteredData.filter((item) => {
          return statusKeysFiltered.some(
            (status) => status.key === formattedStatus(item.status),
          );
        });
      }

      if (contentKeysFiltered.length > 0) {
        filteredData = filteredData.filter((item) => {
          return contentKeysFiltered.some(
            (content) => content.key === item.contentCategoryJoin.label,
          );
        });
      }

      if (typeKeysFiltered.length > 0) {
        filteredData = filteredData.filter((item) => {
          return typeKeysFiltered.some(
            (content) => content.key === formattedTypeContainer(item.type),
          );
        });
      }

      if (typeFillingFilter === "inactive") {
        filteredData = filteredData.filter((item) => {
          return item.statusFilling === typeFillingFilter;
        });
      }

      if (typeFillingFilter === "in_progress") {
        filteredData = filteredData.filter((item) => {
          return (
            item.filling_level > 0 &&
            item.statusFilling !== "critical" &&
            item.statusFilling !== "critical_danger" &&
            item.statusFilling !== "inactive"
          );
        });
      }

      if (
        typeFillingFilter === "critical" ||
        typeFillingFilter === "critical_danger"
      ) {
        filteredData = filteredData.filter((item) => {
          return (
            (item.statusFilling === "critical" ||
              item.statusFilling === "critical_danger") &&
            item.statusFilling !== "inactive"
          );
        });
      }

      if (typeFillingFilter === "empty") {
        filteredData = filteredData.filter((item) => {
          return item.filling_level === 0 && item.statusFilling !== "inactive";
        });
      }

      const getSortValue = (item) => {
        if (fieldSort === "predictionOverflow") {
          const predictionDatetime =
            item.durationFullFillingMs?.[0]?.predictionDatetime;
          if (!predictionDatetime) return Number.MAX_SAFE_INTEGER;
          const ts = new Date(predictionDatetime).getTime();
          return Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts;
        }
        return item[fieldSort];
      };

      const compareAscending = (a, b) => {
        const valA = getSortValue(a);
        const valB = getSortValue(b);
        if (valA < valB) return -1;
        if (valA > valB) return 1;
        return 0;
      };

      const compareDescending = (a, b) => {
        const valA = getSortValue(a);
        const valB = getSortValue(b);
        if (valA > valB) return -1;
        if (valA < valB) return 1;
        return 0;
      };

      let orderFunction;
      if (orderSort === "1") {
        orderFunction = compareAscending;
      } else {
        orderFunction = compareDescending;
      }

      filteredData = filteredData.sort(orderFunction);
      const activeItems = getActiveItems(filteredData, alertNumberOfDays);
      setData([...activeItems]);
    },
    [
      searchTextDebounced,
      positioningKeysFiltered,
      addressKeysFiltered,
      arrayFilterFillingLevel,
      statusKeysFiltered,
      contentKeysFiltered,
      typeKeysFiltered,
      typeFillingFilter,
      fieldSort,
      orderSort,
    ],
  );

  const multiselectTypeRef = useRef();
  const multiselectContentRef = useRef();
  const multiselectAddressRef = useRef();
  const multiselectStatusRef = useRef();
  const multiselectVolumeRef = useRef();
  const multiselectPositioningRef = useRef();
  const multiselectHour = useRef();
  const enrichmentByIdRef = useRef(enrichmentById);

  // Debounce search to avoid heavy filter on every keystroke (300 ms)
  useEffect(() => {
    if (searchText === "") {
      setSearchTextDebounced("");
      return;
    }
    const timer = setTimeout(() => setSearchTextDebounced(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const cleanFilters = () => {
    setSearchText("");
    setSearchTextDebounced("");
    setKeywordFilter("");
    setContentKeysFiltered([]);
    setAddressKeysFiltered([]);
    setStatusKeysFiltered([]);
    setTypeKeysFiltered([]);
    setVolumeKeysFiltered([]);
    setHourKeysFiltered([]);
    setPositioningKeysFiltered([]);
    setArrayFilterFIllingLevel([0, 100]);
    setTypeFillingFilter("all");
    setCleanAllFilters(true);

    multiselectTypeRef.current.resetSelectedValues();
    multiselectContentRef.current.resetSelectedValues();
    multiselectAddressRef.current.resetSelectedValues();
    multiselectStatusRef.current.resetSelectedValues();
    multiselectVolumeRef.current.resetSelectedValues();
    multiselectPositioningRef.current.resetSelectedValues();
    multiselectHour.current.resetSelectedValues();
  };

  const toggleImageZoomOverlay = () => {
    setIsImageZoomOverlayActive(true);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(
    UI_CONSTANTS.PAGINATION.DEFAULT_RECORDS_PER_PAGE,
  );
  const [resultPage, setResultPage] = useState(
    UI_CONSTANTS.PAGINATION.DEFAULT_RECORDS_PER_PAGE,
  );

  const screenWidthToForceBlockDisplay =
    UI_CONSTANTS.RESPONSIVE.SCREEN_WIDTH_FORCE_BLOCK_DISPLAY;

  useEffect(() => {
    setCurrentPage(1);
    setResultPage(recordPerPage);
  }, [recordPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    keywordFilter,
    contentKeysFiltered,
    addressKeysFiltered,
    statusKeysFiltered,
    typeKeysFiltered,
    volumeKeysFiltered,
    hourKeysFiltered,
    positioningKeysFiltered,
    arrayFilterFillingLevel,
    typeFillingFilter,
    searchTextDebounced,
  ]);

  useEffect(
    () => {
      if (keywordF) {
        scrollToContainer(keywordF);
      }
    },
    // eslint-disable-next-line
    [keywordF, fullData],
  );

  useEffect(() => {
    getSuperContainerList()
      .then((response) => {
        if (response.data.superContainers.length > 0) {
          setSuperContainers(response.data.superContainers[0]);
          let containerIdsList = response.data.superContainers.flatMap(
            (superContainer) => superContainer.containerObjectIdList,
          );
          setContainerObjectIdList([...containerIdsList]);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [API, token]);

  const handleDisplayMode = () => {
    setDisplayToBlock(!displayToBlock);
    if (displayMode === "table") {
      setDisplayMode("map");
      Cookies.set("containerDisplayMode", "map", { secure: true });
    } else {
      setDisplayMode("table");
      Cookies.set("containerDisplayMode", "table", { secure: true });
    }
  };

  useEffect(() => {
    if (displayMode === "table") {
      if (screenWidth <= screenWidthToForceBlockDisplay) {
        setDisplayToBlock(true);
      } else {
        setDisplayToBlock(false);
      }
    } else if (displayMode === "map") {
      setDisplayToBlock(true);
    }
  }, [screenWidth, displayMode, screenWidthToForceBlockDisplay]);

  const fetchData = async () => {
    setIsLoading(true);
    setContainersSelected([]);
    setEnrichmentById({});
    enrichmentByIdRef.current = {};

    const filters = [...[], ...[], ...[], ...[], ...[], ...[]];

    await axios
      .all([
        getContainersList({
          page: null,
          limit: null,
          fieldSort: "filling_level",
          orderSort: "-1",
          keywordFilter: JSON.stringify(filters),
          arrayFilterFillingLevel: JSON.stringify([0, 100]),
          typeFillingFilter: "all",
          stopTime:
            hourKeysFiltered.length > 0 ? hourKeysFiltered[0].value : "",
        }),
        getSettingsData(),
      ])
      .then(
        axios.spread((responseData, responseSettings) => {
          if (responseData.status === 200 || responseSettings === 200) {
            const activeItems = getActiveItems(
              responseData.data.data,
              responseSettings.data.settings.alertNumberOfDays,
            );
            setData(activeItems);
            setFullData(responseData.data.data);
            setNoActivePackmat(
              !responseData.data.data.some((item) => item.packmatActive),
            );
            setFillingRateDeliveryMap(
              responseData.data.arrayFillingRateDelivery,
            );
            setKeywordFilterOptions(responseData.data.keywordFilterOptions);
            setSettings(responseSettings.data.settings);
            filterData(
              responseData.data.data,
              responseSettings.data.settings.alertNumberOfDays,
              enrichmentByIdRef.current,
            );
            setIsLoading(false);
          }
        }),
      );
  };

  useEffect(
    () => {
      fetchData();
    },
    // eslint-disable-next-line
    [API, token, hourKeysFiltered],
  );

  useEffect(() => {
    if (
      fullData &&
      fullData.length > 0 &&
      settings &&
      settings.alertNumberOfDays !== undefined
    ) {
      filterData(fullData, settings.alertNumberOfDays, enrichmentById);
    }
    if (cleanAllFilters === true) {
      setCleanAllFilters(false);
    }
  }, [fullData, cleanAllFilters, settings, enrichmentById, filterData]);

  useEffect(() => {
    enrichmentByIdRef.current = enrichmentById;
  }, [enrichmentById]);

  // Function to refresh container data from backend
  const refreshContainerData = useCallback(async () => {
    setEnrichmentById({});
    enrichmentByIdRef.current = {};
    try {
      const filters = keywordFilter || [];

      const responseData = await getContainersList({
        page: null,
        limit: null,
        fieldSort: fieldSort,
        orderSort: orderSort,
        keywordFilter: JSON.stringify(filters),
        arrayFilterFillingLevel: JSON.stringify(arrayFilterFillingLevel),
        typeFillingFilter: typeFillingFilter,
        stopTime: hourKeysFiltered.length > 0 ? hourKeysFiltered[0].value : "",
      });

      if (responseData.status === 200) {
        const newFullData = responseData.data.data;
        setFullData(newFullData);
        setNoActivePackmat(!newFullData.some((item) => item.packmatActive));
        setFillingRateDeliveryMap(responseData.data.arrayFillingRateDelivery);
        setKeywordFilterOptions(responseData.data.keywordFilterOptions);

        return newFullData;
      }
      return null;
    } catch (error) {
      console.error("Error refreshing container data:", error);
      return null;
    }
  }, [
    keywordFilter,
    fieldSort,
    orderSort,
    arrayFilterFillingLevel,
    typeFillingFilter,
    hourKeysFiltered,
  ]);

  const enrichContainersData = useCallback(
    async (dataToEnrich = null) => {
      const containersData = dataToEnrich || fullData;

      if (!containersData || containersData.length === 0 || isLoading) {
        return;
      }

      if (isEnrichingRef.current) {
        return;
      }

      isEnrichingRef.current = true;
      setIsEnriching(true);
      setEnrichmentErrors(new Set());

      try {
        const errors = new Set();
        let newEnrichment = {};

        const enrichContainer = async (container) => {
          try {
            const [enrichmentResponse, cycleResponse, alertResponse] =
              await Promise.allSettled([
                getHistoryFillingSpeed(container._id),
                getHistoryCycleContainer(
                  {
                    currentCycle: true,
                    getLevelLastRotation: true,
                  },
                  container._id,
                ),
                getContainerFillingAlert(container._id),
              ]);

            const entry = parseEnrichmentResponses(
              enrichmentResponse,
              cycleResponse,
              alertResponse,
              container,
            );

            if (entry) {
              return { success: true, containerId: container._id, data: entry };
            }
            return { success: false, containerId: container._id };
          } catch (error) {
            console.error(`Error enriching container ${container._id}:`, error);
            return { success: false, containerId: container._id };
          }
        };

        for (let i = 0; i < containersData.length; i += ENRICHMENT_BATCH_SIZE) {
          const batch = containersData.slice(i, i + ENRICHMENT_BATCH_SIZE);
          const results = await Promise.all(
            batch.map((container) => enrichContainer(container)),
          );

          for (const result of results) {
            if (result.success) {
              newEnrichment[result.containerId] = result.data;
            } else {
              errors.add(result.containerId);
            }
          }
        }

        setEnrichmentById(newEnrichment);
        enrichmentByIdRef.current = newEnrichment;
        setEnrichmentErrors(errors);
        setLastEnrichmentTime(Date.now());

        if (settings?.alertNumberOfDays !== undefined) {
          await filterData(
            dataToEnrich || fullData,
            settings.alertNumberOfDays,
            newEnrichment,
          );
        }
      } catch (error) {
        console.error("Error in enrichContainersData:", error);
      } finally {
        isEnrichingRef.current = false;
        setIsEnriching(false);
      }
    },
    [fullData, isLoading, settings, filterData],
  );

  const retryContainerEnrichment = useCallback(
    async (containerId) => {
      if (
        !fullData ||
        fullData.length === 0 ||
        isLoading ||
        isEnrichingRef.current
      ) {
        return;
      }

      const container = fullData.find((c) => c._id === containerId);
      if (!container) return;

      isEnrichingRef.current = true;
      setIsEnriching(true);
      setEnrichmentErrors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(containerId);
        return newSet;
      });

      try {
        const [enrichmentResponse, cycleResponse, alertResponse] =
          await Promise.allSettled([
            getHistoryFillingSpeed(containerId),
            getHistoryCycleContainer(
              {
                currentCycle: true,
                getLevelLastRotation: true,
              },
              containerId,
            ),
            getContainerFillingAlert(containerId),
          ]);

        const entry = parseEnrichmentResponses(
          enrichmentResponse,
          cycleResponse,
          alertResponse,
          container,
        );

        if (entry) {
          const updatedEnrichment = {
            ...enrichmentByIdRef.current,
            [containerId]: entry,
          };
          setEnrichmentById(updatedEnrichment);
          enrichmentByIdRef.current = updatedEnrichment;
          setLastEnrichmentTime(Date.now());

          if (settings?.alertNumberOfDays !== undefined) {
            await filterData(
              fullData,
              settings.alertNumberOfDays,
              updatedEnrichment,
            );
          }
        } else {
          setEnrichmentErrors((prev) => new Set(prev).add(containerId));
        }
      } catch (error) {
        console.error(
          `Error retrying enrichment for container ${containerId}:`,
          error,
        );
        setEnrichmentErrors((prev) => new Set(prev).add(containerId));
      } finally {
        isEnrichingRef.current = false;
        setIsEnriching(false);
      }
    },
    [fullData, isLoading, settings, filterData],
  );

  // Load all enrichment data for all containers (shared between planning and table)
  // Load proactively so data is available for both views
  const enrichContainersDataRef = useRef(enrichContainersData);
  useEffect(() => {
    enrichContainersDataRef.current = enrichContainersData;
  }, [enrichContainersData]);

  useEffect(() => {
    if (!fullData || fullData.length === 0 || isLoading) {
      return;
    }
    enrichContainersDataRef.current();
  }, [fullData, isLoading]);

  // Trigger enrichment when switching tabs
  useEffect(() => {
    if (activeMonitoringTab === "zones" || activeMonitoringTab === "planning") {
      enrichContainersDataRef.current();
    }
    if (activeMonitoringTab === "history") {
      setShareButton(null);
    }
  }, [activeMonitoringTab]);

  const scrollToContainer = async (id) => {
    setRecordPerPage(100);
    const container = fullData.filter((item) => item._id === id);
    const element = document.getElementById(`${id}`);
    element?.scrollIntoView({
      behavior: "smooth",
    });
    setContainerActive(container[0]);
  };

  const displayContainer = () => {
    const leftIndex = data.findIndex((item) =>
      item.image_url.includes("-L.jpeg"),
    );
    const rightIndex = data.findIndex((item) =>
      item.image_url.includes("-R.jpeg"),
    );
    if (leftIndex !== -1) {
      return leftIndex;
    } else {
      if (rightIndex !== -1) {
        return rightIndex;
      } else {
        return null;
      }
    }
  };

  useEffect(() => {
    const inactiveItems = fullData.filter((item) => {
      const lastUpdate = new Date(item._time);
      if (!item._time || Number.isNaN(lastUpdate.getTime())) {
        return true;
      }

      const deltaAlert = Math.floor(
        (Date.now() - lastUpdate.getTime()) / 86400000,
      );
      return deltaAlert > settings.alertNumberOfDays;
    });

    setContainerInactive(inactiveItems);
  }, [fullData, settings.alertNumberOfDays]);

  if (token) {
    return (
      <div
        className="settings-page-wrapper"
        style={{
          minWidth: "850px",
        }}
      >
        <PageHeader
          title={t("My Monitoring")}
          tabs={[
            { id: "zones", label: t("Active Zones") },
            { id: "planning", label: t("Filling Planning") },
            { id: "history", label: t("Historique") },
          ]}
          activeTab={activeMonitoringTab}
          onTabChange={(tabId) => setActiveMonitoringTab(tabId)}
          shareButton={shareButton}
        />
        <div
          className="settings-content-wrapper"
          style={{
            position: "relative",
            overflowX: screenWidth < 1080 ? "auto" : "visible",
            whiteSpace: monitoring && "nowrap",
          }}
        >
          {!monitoring &&
          !(isAkanthasAccount && viewMode === "admin") &&
          location.pathname.includes("containers") ? (
            <ForbiddenModule viewType={"monitoring"} />
          ) : (
            <>
              {activeMonitoringTab !== "history" && (
                <ContainerFilter
                  arrayFilterFillingLevel={arrayFilterFillingLevel}
                  setArrayFilterFIllingLevel={setArrayFilterFIllingLevel}
                  keywordFilterOptions={keywordFilterOptions}
                  keywordFilter={keywordFilter}
                  setKeywordFilter={setKeywordFilter}
                  contentKeysFiltered={contentKeysFiltered}
                  setContentKeysFiltered={setContentKeysFiltered}
                  addressKeysFiltered={addressKeysFiltered}
                  setAddressKeysFiltered={setAddressKeysFiltered}
                  statusKeysFiltered={statusKeysFiltered}
                  setStatusKeysFiltered={setStatusKeysFiltered}
                  typeKeysFiltered={typeKeysFiltered}
                  setTypeKeysFiltered={setTypeKeysFiltered}
                  volumeKeysFiltered={volumeKeysFiltered}
                  setVolumeKeysFiltered={setVolumeKeysFiltered}
                  positioningKeysFiltered={positioningKeysFiltered}
                  setPositioningKeysFiltered={setPositioningKeysFiltered}
                  cleanFilters={cleanFilters}
                  cleanAllFilters={cleanAllFilters}
                  setCleanAllFilters={setCleanAllFilters}
                  setTypeFillingFilter={setTypeFillingFilter}
                  multiselectTypeRef={multiselectTypeRef}
                  multiselectContentRef={multiselectContentRef}
                  multiselectAddressRef={multiselectAddressRef}
                  multiselectStatusRef={multiselectStatusRef}
                  multiselectVolumeRef={multiselectVolumeRef}
                  multiselectPositioningRef={multiselectPositioningRef}
                  hourKeysFiltered={hourKeysFiltered}
                  setHourKeysFiltered={setHourKeysFiltered}
                  multiselectHour={multiselectHour}
                  data={data}
                  settings={settings}
                  displayToBlock={displayToBlock}
                  handleDisplayMode={handleDisplayMode}
                  displayMode={displayMode}
                  screenWidth={screenWidth}
                  screenWidthToForceBlockDisplay={
                    screenWidthToForceBlockDisplay
                  }
                  activeMonitoringTab={activeMonitoringTab}
                  lastEnrichmentTime={lastEnrichmentTime}
                  onRefresh={async () => {
                    setIsEnriching(true);
                    setLastEnrichmentTime(Date.now());
                    try {
                      const newData = await refreshContainerData();
                      if (newData) {
                        // Mettre à jour data avec les nouvelles données filtrées
                        // Utiliser settings.alertNumberOfDays si disponible, sinon une valeur par défaut
                        const alertDays = settings?.alertNumberOfDays ?? 30;
                        await filterData(newData, alertDays, {});
                        await enrichContainersData(newData);
                      } else {
                        setIsEnriching(false);
                      }
                    } catch (error) {
                      console.error("Error during refresh:", error);
                      setIsEnriching(false);
                    }
                  }}
                  isEnriching={isEnriching}
                  fieldSort={fieldSort}
                  setFieldSort={setFieldSort}
                  onShareButtonRender={setShareButton}
                />
              )}

              {activeMonitoringTab === "zones" && isLoading ? (
                <div
                  className="flex items-center justify-center"
                  style={{ backgroundColor: "#F5F5F5", minHeight: "60vh" }}
                >
                  <div className="relative">
                    <div
                      className="w-12 h-12 border-[3px] border-[#00A7A6] border-t-transparent rounded-full animate-spin"
                      role="status"
                      aria-label="Chargement en cours"
                    >
                      <span className="sr-only">Chargement...</span>
                    </div>
                    <div className="absolute inset-0 w-12 h-12 border-[3px] border-[#00A7A6] border-opacity-20 rounded-full animate-ping" />
                  </div>
                </div>
              ) : activeMonitoringTab === "zones" ? (
                <div className="w-full">
                  {!displayToBlock && (
                    <>
                      <section className="top-table top-table--cols-5 mt-6">
                        <div
                          style={{
                            paddingTop: "24px",
                            paddingBottom: "24px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "8px",
                            backgroundColor: "#fff",
                            borderRadius: "8px 8px 0 0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            <div className="search-input-wrapper">
                              <i className="fas fa-search search-input-icon" />
                              <input
                                type="text"
                                className="search-input"
                                value={searchText}
                                placeholder={`${t("Search")} (${t("Flux")}, ${t("Address")})`}
                                onChange={(e) => {
                                  setSearchText(e.target.value);
                                }}
                                style={{ height: "40px" }}
                              />
                            </div>
                            {containersSelected.length > 0 && (
                              <>
                                <button
                                  className={`relative flex bg-cyan rounded-md text-white my-2 transition-opacity ${checkPlanningIsActive(data, containersSelected) ? "opacity-100 hover:bg-yellow" : "opacity-20 cursor-not-allowed hover:bg-gainsboro-transparent"}`}
                                  onMouseOver={() => setHelpButton(true)}
                                  onMouseOut={() => setHelpButton(false)}
                                  disabled={
                                    !checkPlanningIsActive(
                                      data,
                                      containersSelected,
                                    )
                                  }
                                  onClick={() =>
                                    setConfirmationCollectModal(true)
                                  }
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <TruckSvg
                                    color={"#FFF"}
                                    width={25}
                                    height={25}
                                  />
                                  <span className="font-semibold text-sm">
                                    {t("Schedule")}
                                  </span>
                                  {helpButton &&
                                    !checkContainerContractExist(
                                      data,
                                      containersSelected,
                                    ) && (
                                      <div className="absolute bg-yellow rounded-md w-96 -top-8 left-20 p-2 text-sm">
                                        {t(
                                          "Activate by entering the email in your contracts",
                                        )}
                                      </div>
                                    )}
                                </button>
                                <div className="flex px-2 border-solid border-light-grey rounded-full border-1 my-2 items-center">
                                  <FontAwesomeIcon
                                    icon="times"
                                    className="close-modal text-grey mx-1 cursor-pointer"
                                    onClick={() => {
                                      handleDeselectAll(
                                        data,
                                        setData,
                                        setContainersSelected,
                                      );
                                    }}
                                  />
                                  <p className="my-auto mx-1 text-sm text-grey">
                                    {containersSelected.length}{" "}
                                    {t("Selection").toLowerCase()}
                                    {containersSelected.length > 1 && "s"}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </section>
                      <ContainerHeader
                        data={data}
                        setData={setData}
                        setContainersSelected={setContainersSelected}
                        selectedAll={checkIfAllSelected(
                          data,
                          containersSelected,
                        )}
                        displayToBlock={displayToBlock}
                        noActivePackmat={noActivePackmat}
                      />
                    </>
                  )}

                  <section
                    className={`content-card ${displayToBlock ? "table-display-block mt-4" : ""}`}
                  >
                    <article
                      className={`content-table ${displayToBlock ? "content-table-block" : ""}`}
                      style={{
                        width:
                          screenWidth < 1080 ||
                          (screenWidth >= 1080 &&
                            screenWidth <= screenWidthToForceBlockDisplay)
                            ? "100%"
                            : displayToBlock &&
                                screenWidth > screenWidthToForceBlockDisplay
                              ? "60%"
                              : "",
                      }}
                    >
                      {data.length > 0 ? (
                        <>
                          {data
                            .slice(
                              (currentPage - 1) * recordPerPage,
                              currentPage * recordPerPage,
                            )
                            .map((container, index) => {
                              let deltaAlert = Math.floor(
                                (new Date()?.getTime() -
                                  new Date(container._time)?.getTime()) /
                                  86400000,
                              );
                              if (deltaAlert <= settings.alertNumberOfDays) {
                                return (
                                  <div
                                    key={container._id}
                                    className={displayToBlock ? "mb-2.5" : ""}
                                  >
                                    {modalEdit &&
                                      formData._id ===
                                        container._measurement && (
                                        <ContainerEdit
                                          tableData={data}
                                          setTableData={setData}
                                          editFormData={formData}
                                          setEditFormData={setFormData}
                                          modalEdit={modalEdit}
                                          setModalEdit={setModalEdit}
                                        />
                                      )}

                                    <div
                                      style={{
                                        display:
                                          Object.keys(superContainers).length >
                                            0 &&
                                          displayContainer() !== index &&
                                          location.pathname.includes(
                                            "characterization",
                                          ) &&
                                          "none",
                                      }}
                                    >
                                      <ContainerRow
                                        containersSelected={containersSelected}
                                        setContainersSelected={
                                          setContainersSelected
                                        }
                                        setConfirmationCollectModal={
                                          setConfirmationCollectModal
                                        }
                                        data={container}
                                        displayToBlock={displayToBlock}
                                        formData={formData}
                                        setFormData={setFormData}
                                        setModalEdit={setModalEdit}
                                        fullData={fullData}
                                        tableData={data}
                                        setTableData={setData}
                                        settings={settings}
                                        screenWidth={screenWidth}
                                        noActivePackmat={noActivePackmat}
                                        toggleImageZoomOverlay={
                                          toggleImageZoomOverlay
                                        }
                                        containerActive={containerActive}
                                        setContainerActive={setContainerActive}
                                        superContainer={
                                          location.pathname.includes(
                                            "characterization",
                                          ) &&
                                          containerObjectIdList.includes(
                                            container._id,
                                          )
                                            ? superContainers
                                            : false
                                        }
                                        enrichmentErrors={enrichmentErrors}
                                        retryEnrichment={
                                          retryContainerEnrichment
                                        }
                                        isEnriching={isEnriching}
                                      />
                                    </div>
                                  </div>
                                );
                              } else {
                                return <React.Fragment></React.Fragment>;
                              }
                            })}
                          {containerInactive.length > 0 &&
                            Math.ceil(data.length / recordPerPage) ===
                              currentPage && (
                              <>
                                <div
                                  className={`flex w-full cursor-pointer ${displayToBlock ? "border-[1.2px] border-[#D3D3D3] rounded-lg" : ""}`}
                                  style={{
                                    padding: "0",
                                    minHeight: "60px",
                                    backgroundColor: "#fff",
                                    borderBottom: displayToBlock
                                      ? "1.2px solid #D3D3D3"
                                      : "1.5px solid #e0e0e0",
                                    borderLeft: displayToBlock
                                      ? "1.2px solid #D3D3D3"
                                      : "1.5px solid #e0e0e0",
                                    borderRight: displayToBlock
                                      ? "1.2px solid #D3D3D3"
                                      : "1.5px solid #e0e0e0",
                                    borderTop: displayToBlock
                                      ? "1.2px solid #D3D3D3"
                                      : "none",
                                    alignItems: "center",
                                    margin: 0,
                                    marginTop: displayToBlock ? "0.6rem" : 0,
                                    marginBottom:
                                      displayToBlock && showContainerInactive
                                        ? "1.2rem"
                                        : 0,
                                  }}
                                  onClick={() =>
                                    setShowContainerInactive(
                                      !showContainerInactive,
                                    )
                                  }
                                >
                                  <div className="w-full flex items-center justify-center h-full text-[#0F265C]">
                                    <span
                                      style={{ fontSize: "14px" }}
                                    >{`${t("Other containers")} (${containerInactive.length} ${t(containerInactive.length > 1 ? "inactive_plural" : "inactive_singular")})`}</span>
                                    <i
                                      className={`fas fa-chevron-down ml-2 ${showContainerInactive ? "rotate-180" : ""}`}
                                      style={{ fontSize: "12px" }}
                                    ></i>
                                  </div>
                                </div>
                                {showContainerInactive &&
                                  containerInactive.map((container, index) => {
                                    return (
                                      <div
                                        key={container._id}
                                        className={
                                          displayToBlock ? "mb-2.5" : ""
                                        }
                                        style={{
                                          marginBottom:
                                            displayToBlock &&
                                            index ===
                                              containerInactive.length - 1
                                              ? "1.2rem"
                                              : undefined,
                                        }}
                                      >
                                        {modalEdit &&
                                          formData._id ===
                                            container._measurement && (
                                            <ContainerEdit
                                              tableData={data}
                                              setTableData={setData}
                                              editFormData={formData}
                                              setEditFormData={setFormData}
                                              modalEdit={modalEdit}
                                              setModalEdit={setModalEdit}
                                            />
                                          )}

                                        <div
                                          style={{
                                            display:
                                              Object.keys(superContainers)
                                                .length > 0 &&
                                              displayContainer() !== index &&
                                              location.pathname.includes(
                                                "characterization",
                                              ) &&
                                              "none",
                                          }}
                                        >
                                          <ContainerRow
                                            containersSelected={
                                              containersSelected
                                            }
                                            setContainersSelected={
                                              setContainersSelected
                                            }
                                            setConfirmationCollectModal={
                                              setConfirmationCollectModal
                                            }
                                            data={container}
                                            displayToBlock={displayToBlock}
                                            formData={formData}
                                            setFormData={setFormData}
                                            setModalEdit={setModalEdit}
                                            fullData={fullData}
                                            tableData={data}
                                            setTableData={setData}
                                            settings={settings}
                                            screenWidth={screenWidth}
                                            noActivePackmat={noActivePackmat}
                                            toggleImageZoomOverlay={
                                              toggleImageZoomOverlay
                                            }
                                            containerActive={containerActive}
                                            setContainerActive={
                                              setContainerActive
                                            }
                                            superContainer={
                                              location.pathname.includes(
                                                "characterization",
                                              ) &&
                                              containerObjectIdList.includes(
                                                container._id,
                                              )
                                                ? superContainers
                                                : false
                                            }
                                            enrichmentErrors={enrichmentErrors}
                                            retryEnrichment={
                                              retryContainerEnrichment
                                            }
                                            isEnriching={isEnriching}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                              </>
                            )}
                        </>
                      ) : (
                        <section className={`error-form display-error`}>
                          <p>{t("No container")}</p>
                        </section>
                      )}
                    </article>

                    {data.length > 0 &&
                      displayMode === "table" &&
                      !displayToBlock &&
                      activeMonitoringTab === "zones" && (
                        <div className="table-pagination-bottom">
                          <Pagination
                            totalEntry={data.length}
                            recordPerPage={recordPerPage}
                            setRecordPerPage={setRecordPerPage}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            resultPage={resultPage}
                            setResultPage={setResultPage}
                          />
                        </div>
                      )}

                    {displayToBlock &&
                      screenWidth > screenWidthToForceBlockDisplay && (
                        <section
                          className="card-block-display ml-4"
                          style={{ marginTop: "8px" }}
                        >
                          <section className="card-block">
                            <Card
                              className="content-map"
                              style={{
                                border: "1.2px solid #9CA3AF",
                                borderRadius: "8px",
                                height: "calc(99vh / var(--app-zoom, 1))",
                              }}
                            >
                              {isImageZoomOverlayActive ? (
                                <div style={{ height: "100%" }}>
                                  <ContainerImageZoom
                                    setIsImageZoomOverlayActive={
                                      setIsImageZoomOverlayActive
                                    }
                                    setContainerActive={setContainerActive}
                                    containerActive={containerActive}
                                  />
                                </div>
                              ) : (
                                <MapDisplay
                                  fillingRateDelivery={fillingRateDeliveryMap}
                                  markers={data}
                                  mapHeight={"99vh"}
                                  setContainerActive={setContainerActive}
                                  scrollToContainer={scrollToContainer}
                                  cleanFilters={cleanFilters}
                                  isContainer={true}
                                  fullData={fullData}
                                  orderSort={orderSort}
                                  setData={setData}
                                />
                              )}
                            </Card>
                          </section>
                        </section>
                      )}
                  </section>
                </div>
              ) : null}

              {activeMonitoringTab === "planning" && (
                <div className="w-full mt-4">
                  <FillingCalendar containers={data} />
                </div>
              )}

              {activeMonitoringTab === "history" && <ActivityHistory />}

              {confimationCollectModal && (
                <ConfirmationModal
                  open={confimationCollectModal}
                  setOpen={setConfirmationCollectModal}
                  title={t("Schedule a rotation")}
                  message={modalMessage}
                  submitText={t("Schedule a rotation")}
                  handleSubmit={() => {
                    sendPlanningMail(containersSelected, "rotation");
                    const newData = data.forEach((elt) => {
                      containersSelected.forEach((container) => {
                        if (elt._id === container._id) {
                          elt.status = "remove_driver_exchange";
                        }
                      });
                    });
                    setData(newData);
                    handleDeselectAll(data, setData, setContainersSelected);
                  }}
                  close={() =>
                    handleDeselectAll(data, setData, setContainersSelected)
                  }
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }
};

export default withRouter(Container);
